import type { WhatsAppCounterRow, WhatsAppMessageRow } from "../whatsappTypes";

/**
 * Almikia Epic EW2 (TASK-080): pure rules of the 'Mensajes de atencion del mes' counter. The
 * production numbers come from the TASK-076 `whatsapp_monthly_counter` function; the demo
 * (FakeRest) computes the same figures from its rows with these helpers. The month is the
 * Mexico City calendar month, and every function takes an injectable `now` (never the clock).
 */
export const MEXICO_CITY_TIME_ZONE = "America/Mexico_City";

/** Month shares that trigger a warning: 80 % and 100 % of the free quota. */
export const WARN_PERCENT = 80;

const MONTHS_OFFERED = 12;
/** Noon in Mexico City (UTC-6) on the 15th: far from any month boundary, whatever the offset. */
const MID_MONTH_UTC_HOUR = 18;

export type CounterLevel = "ok" | "warning" | "full";

export type MonthOption = {
  /** `YYYY-MM` in Mexico City time. */
  key: string;
  label: string;
  /** The instant sent to the counter function as `p_now` for that month. */
  anchor: string;
};

const monthParts = (date: Date): { year: number; month: number } => {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: MEXICO_CITY_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
  }).formatToParts(date);
  const read = (type: string) =>
    Number(parts.find((part) => part.type === type)?.value);
  return { year: read("year"), month: read("month") };
};

/** `YYYY-MM` of the Mexico City calendar month an instant falls in. */
export const mexicoCityMonthKey = (date: Date): string => {
  const { year, month } = monthParts(date);
  return `${year}-${String(month).padStart(2, "0")}`;
};

const monthLabel = (anchor: Date): string => {
  const text = new Intl.DateTimeFormat("es-MX", {
    timeZone: MEXICO_CITY_TIME_ZONE,
    month: "long",
    year: "numeric",
  }).format(anchor);
  return text.charAt(0).toUpperCase() + text.slice(1);
};

/**
 * The months a Direccion user can pick, newest first, starting with the current Mexico City
 * month (whose anchor is `now` itself) and going back `count - 1` months.
 */
export const buildMonthOptions = (
  now: Date,
  count: number = MONTHS_OFFERED,
): MonthOption[] => {
  const { year, month } = monthParts(now);
  return Array.from({ length: count }, (_, back) => {
    const index = year * 12 + (month - 1) - back;
    const optionYear = Math.floor(index / 12);
    const optionMonth = (index % 12) + 1;
    const anchor =
      back === 0
        ? now
        : new Date(
            Date.UTC(optionYear, optionMonth - 1, 15, MID_MONTH_UTC_HOUR),
          );
    return {
      key: `${optionYear}-${String(optionMonth).padStart(2, "0")}`,
      label: monthLabel(anchor),
      anchor: anchor.toISOString(),
    };
  });
};

/** Whether an ISO instant falls in the Mexico City month of `anchor`. */
export const isInMonthOf = (iso: string, anchor: Date): boolean =>
  mexicoCityMonthKey(new Date(iso)) === mexicoCityMonthKey(anchor);

/** `warning` from 80 % of the quota, `full` once it is used up (a zero quota is always full). */
export const counterLevel = (used: number, quota: number): CounterLevel => {
  if (quota <= 0 || used >= quota) return "full";
  return used * 100 >= quota * WARN_PERCENT ? "warning" : "ok";
};

/** Progress bar value, 0-100 (whole percent, never past the bar). */
export const usagePercent = (used: number, quota: number): number =>
  quota <= 0 ? 100 : Math.min(100, Math.floor((used * 100) / quota));

/**
 * The demo counter: per business number, the CRM free-text messages (service messages) and the
 * paid templates sent in the Mexico City month of `anchor`. Messages from Pepe's app (origin
 * `app`, echoes) and templates are never service messages; free templates are not paid ones.
 */
export const buildDemoCounterRows = (
  messages: readonly WhatsAppMessageRow[],
  numberIds: readonly string[],
  monthlyFreeQuota: number,
  anchor: Date,
): WhatsAppCounterRow[] => {
  const { year, month } = monthParts(anchor);
  const monthStart = new Date(Date.UTC(year, month - 1, 1, 6));
  const monthEnd = new Date(Date.UTC(year, month, 1, 6));
  return numberIds.map((numberId) => {
    const own = messages.filter(
      (message) =>
        message.phone_number_id === numberId &&
        message.origin === "crm" &&
        isInMonthOf(message.message_at, anchor),
    );
    return {
      monthStart: monthStart.toISOString(),
      monthEnd: monthEnd.toISOString(),
      serviceMessages: own.filter((message) => message.kind === "free_text")
        .length,
      monthlyFreeQuota,
      paidTemplates: own.filter(
        (message) =>
          (message.kind === "template" ||
            message.kind === "welcome_template") &&
          message.cost_label === "Con costo",
      ).length,
    };
  });
};
